import React, { useMemo, useState, Suspense, useEffect } from "react";
import { View, Text, Pressable, ScrollView, Platform, Dimensions, StyleSheet } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useDispatch } from "react-redux";
import { fetchMyTickets } from "../store/thunks/ticketingPortalThunk";
import { setSelectedTicketId, setTicketDetailSource } from "../store/slices/ticketingPortalSlice";
import LHLoader from "./LHLoader";
import styles from "../screens/TicketingPortal/TicketingPortalScreen.style";
import mobileStyles from "../screens/TicketingPortal/TicketingPortalScreen.mobile.style";
import ActionTile from "../screens/TicketingPortal/components/ActionTile";
import { LandingActionView } from "../screens/TicketingPortal/hooks/useTicketingPortalLandingModel";

const SubmitTicketScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.submit-ticket" */
  "../screens/TicketingPortal/SubmitTicketScreen"
));

const MyTicketsScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.my-tickets" */
  "../screens/TicketingPortal/MyTicketsScreen"
));

const TicketDetailScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.ticket-detail" */
  "../screens/TicketingPortal/TicketDetailScreen"
));

type ActiveView = "landing" | "Submit Ticket" | "My Tickets" | "Ticket Detail";

const LIMITED_ACTIONS: LandingActionView[] = [
  {
    id: "submit-ticket",
    title: "Submit Ticket",
    description:
      "Report an issue or request support for your site. PepGPT can help triage before you submit.",
    metric: "",
    icon: "note-add",
    iconBackgroundColor: "#dce8fb",
    iconColor: "#156fd5",
  },
  {
    id: "my-tickets",
    title: "My Tickets",
    description:
      "Track status and progress of tickets you've submitted or are a stakeholder on.",
    metric: "",
    icon: "confirmation-number",
    iconBackgroundColor: "#d9ecf8",
    iconColor: "#0f87c5",
  },
];

/** Formats an ISO date string as MM/DD/YYYY (device-local), per 175155 banner copy. */
const formatShortDate = (iso?: string): string => {
  if (!iso) return "";
  const utcIso = /Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${month}/${day}/${d.getFullYear()}`;
};

const LimitedAccessPortal: React.FC = () => {
  const [activeView, setActiveView] = useState<ActiveView>("landing");
  const authUser = useSelector((state: RootState) => state.auth.user);
  const basicAccessUser = useSelector((state: RootState) => state.appData.basicAccessUser);
  const myTicketsTotalElements = useSelector((state: RootState) => state.ticketingPortal.myTicketsTotalElements);
  const dispatch = useDispatch();
  const [hasOpenTickets, setHasOpenTickets] = useState<boolean | null>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketCheckKey, setTicketCheckKey] = useState(0);
  const [isDeepLinkedTicket, setIsDeepLinkedTicket] = useState(false);

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  // Handle deep-linked tickets from URL
  useEffect(() => {
    const parseTicketUrl = () => {
      if (typeof window === "undefined") return null;
      const path = window.location.pathname;
      const ticketMatch = path.match(/\/ticket-details\/ticket-id\/([A-Z0-9\-]+)/);
      if (ticketMatch && ticketMatch[1]) {
        return ticketMatch[1];
      }
      return null;
    };

    const deepLinkedTicketId = parseTicketUrl();
    if (deepLinkedTicketId) {
      dispatch(setSelectedTicketId(deepLinkedTicketId) as any);
      dispatch(setTicketDetailSource("Submit Ticket") as any);
      setIsDeepLinkedTicket(true);
      setActiveView("Ticket Detail");
    }
  }, [dispatch]);

  // Most recent Access Request ticket still awaiting Sustain review — drives the status banner (175155).
  const openAccessRequestTicket = useMemo(() => {
    return tickets.find((t) => {
      const status = String(t?.status || "").toLowerCase();
      return t?.category === "Access Request" && status !== "resolved" && status !== "closed";
    });
  }, [tickets]);

  const handleViewAccessRequestTicket = () => {
    if (!openAccessRequestTicket) return;
    dispatch(setSelectedTicketId(openAccessRequestTicket.id) as any);
    dispatch(setTicketDetailSource("Submit Ticket") as any);
    setActiveView("Ticket Detail");
  };

  const greeting = "Ticketing Portal";

  const subtitle = "Report an issue or track a request you have already submitted.";

  // My Tickets tile badge/count line — mirrors useTicketingPortalLandingModel's live count.
  const myTicketsTotal = myTicketsTotalElements > 0 ? myTicketsTotalElements : tickets.length;
  const resolvedActions: LandingActionView[] = useMemo(
    () => LIMITED_ACTIONS.map((action) =>
      action.id === "my-tickets"
        ? {
            ...action,
            badgeText: String(myTicketsTotal),
            metric: `${myTicketsTotal} ticket${myTicketsTotal === 1 ? "" : "s"}`,
          }
        : action
    ),
    [myTicketsTotal]
  );

  const accessRequestBanner = openAccessRequestTicket ? (
    <View style={localStyles.arBanner}>
      <MaterialIcons name="info-outline" size={20} color="#0f6fd5" style={{ marginTop: 2 }} />
      <View style={{ flex: 1, marginLeft: 12 }}>
        <Text style={localStyles.arBannerTitle}>Your access request is with the Sustain team.</Text>
        <Text style={localStyles.arBannerSubtitle}>
          Ticket {openAccessRequestTicket.id}, submitted {formatShortDate(openAccessRequestTicket.submitted)}. You will get a Teams message when it is approved.
        </Text>
      </View>
      <Pressable style={localStyles.arBannerButton} onPress={handleViewAccessRequestTicket}>
        <Text style={localStyles.arBannerButtonText}>View Ticket</Text>
      </Pressable>
    </View>
  ) : null;


  const navigateTo = (screen: string) => {
    // "Ticketing Portal" is the back-navigation target used by sub-screens
    // (e.g. SubmitTicketScreen breadcrumb). Map it to the landing view so
    // clicking the breadcrumb returns to the 2-tile landing page instead of
    // rendering a blank screen.
    if (screen === "Ticketing Portal") {
      setActiveView("landing");
      setTicketCheckKey((k) => k + 1);
      return;
    }
    // After ticket submission SubmitTicketScreen calls onNavigate("Ticket Detail"); skip it and
    // return to landing instead, re-checking tickets so the tile set stays up to date.
    // From My Tickets, a row click should open the ticket (read-only, enforced in TicketDetailScreen).
    if (screen === "Ticket Detail") {
      if (activeView === "My Tickets") {
        setActiveView("Ticket Detail");
        return;
      }
      setActiveView("landing");
      setHasOpenTickets(null);
      setTicketCheckKey((k) => k + 1);
      return;
    }
    setActiveView(screen as ActiveView);
  };

  useEffect(() => {
    // Only run when basicAccessUser is present (we want to check user's tickets)
    if (!basicAccessUser) return;
    const check = async () => {
      try {
        const submitterEmail = basicAccessUser?.userEmail || (authUser as any)?.email || (authUser as any)?.username;
        if (!submitterEmail) {
          setHasOpenTickets(false);
          return;
        }
        const res = await (dispatch(fetchMyTickets({ submitterEmail, pageSize: 50 })) as any);
        const fetchedTickets = res?.tickets ?? [];
        setTickets(fetchedTickets);
        // Gate strictly on the ticketing/tickets content array (empty vs non-empty),
        // per the Navi/IDM access-gating states — not on individual ticket status.
        const hasTicketRecords = fetchedTickets.length > 0;
        setHasOpenTickets(hasTicketRecords);
        // If user has no ticket records, auto-open Submit Ticket and pre-populate + lock fields
        if (!hasTicketRecords) {
          // dispatch prefill and lock — import actions from slice lazily to avoid circular import
          const { prePopulateSubmitTicket, setSubmitTicketLockedFields } = await import("../store/slices/ticketingPortalSlice");
          dispatch(prePopulateSubmitTicket({
            title: "Access Request for New User",
            description: "I need access to Navi for my role. Please grant basic access.",
            stakeholders: "",
            category: "Access Request",
            subCategory: "New Access",
            level: "Location",
            levelReference: "",
            urgency: "Low",
            impactedPeople: "1",
            loadsImpacted: "None",
            reopenedFromId: "",
            submitter: submitterEmail,
          }) as any);
          dispatch(setSubmitTicketLockedFields({ title: true, category: true, subCategory: true, submitter: true }) as any);
          setActiveView("Submit Ticket");
        }
      } catch (e) {
        setHasOpenTickets(false);
      }
    };
    void check();
  // ticketCheckKey is incremented after submission to force a re-check of tickets,
  // transitioning from State 2 (no tickets) to State 3 (both tiles) on the landing page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicAccessUser, authUser, dispatch, ticketCheckKey]);

  // Ensure locks are applied when the portal renders Submit Ticket inline
  useEffect(() => {
    const ensureLocks = async () => {
      if (hasOpenTickets === false) {
        const submitterEmail = basicAccessUser?.userEmail || (authUser as any)?.email || (authUser as any)?.username;
        const { setSubmitTicketLockedFields, prePopulateSubmitTicket } = await import("../store/slices/ticketingPortalSlice");
        dispatch(setSubmitTicketLockedFields({ title: true, category: true, subCategory: true, submitter: true }) as any);
        // ensure draft has sensible defaults if not already populated
        dispatch(prePopulateSubmitTicket({
          title: "Access Request for New User",
          description: "I need access to Navi for my role. Please grant basic access.",
          stakeholders: "",
          category: "Access Request",
          subCategory: "New Access",
          level: "Location",
          levelReference: "",
          urgency: "Low",
          impactedPeople: "1",
          loadsImpacted: "None",
          reopenedFromId: "",
          submitter: submitterEmail,
        }) as any);
      }
    };
    void ensureLocks();
  }, [hasOpenTickets, dispatch]);

  const handleTilePress = (actionId: string) => {
    if (actionId === "submit-ticket") {
      setActiveView("Submit Ticket");
    }
    if (actionId === "my-tickets") {
      setActiveView("My Tickets");
    }
  };

  // While tickets API is still loading (hasOpenTickets === null), block all screens from rendering
  // This prevents ANY screen from briefly showing before the access-check completes, eliminating flicker
  if (hasOpenTickets === null) {
    return <LHLoader testID="limited-access-loading" />;
  }

  if (activeView !== "landing") {
    return (
      <View style={styles.screenRoot}>
        {/* Back header — hidden for the locked, single-purpose access-request flow (no ticket records yet) */}
        {hasOpenTickets !== false ? (
          <View style={localStyles.backHeader}>
            <Pressable onPress={() => setActiveView("landing")} style={localStyles.backButton}>
              <MaterialIcons name="arrow-back" size={20} color="#156fd5" />
              <Text style={localStyles.backText}>Back to Portal</Text>
            </Pressable>
          </View>
        ) : null}

        <Suspense fallback={<LHLoader />}>
          {activeView === "Submit Ticket" && <SubmitTicketScreen onNavigate={navigateTo} isFirstTimeUser={hasOpenTickets === false} />}
          {activeView === "My Tickets" && <MyTicketsScreen onNavigate={navigateTo} />}
          {activeView === "Ticket Detail" && <TicketDetailScreen onNavigate={navigateTo} isDeepLinked={isDeepLinkedTicket} />}
        </Suspense>
      </View>
    );
  }

  /* ── iPhone mobile layout ─────────────────────────────────── */
  if (isiPhone) {
    return (
      <View style={styles.screenRoot}>
        <View style={styles.container}>
          <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
            {/* Hero card */}
            <View style={mobileStyles.heroBlock}>
              <Text style={mobileStyles.heroLabel}>TICKETING PORTAL</Text>
              <Text style={mobileStyles.heroGreeting}>{greeting}</Text>
              <Text style={mobileStyles.heroSubtitle}>{subtitle}</Text>
            </View>

            {accessRequestBanner}

            <Text style={styles.sectionLabel}>WHAT WOULD YOU LIKE TO DO?</Text>

            {/* Compact horizontal action tiles — hide My Tickets for State 2 users (no prior tickets) */}
            <View style={mobileStyles.gridContainer}>
              {resolvedActions
                .filter((a) => {
                  if (a.id !== "my-tickets") return true;
                  if (hasOpenTickets === null) return !!(myTicketsTotalElements && myTicketsTotalElements > 0);
                  return hasOpenTickets;
                })
                .map((action: LandingActionView) => (
                  <ActionTile
                    key={action.id}
                    action={action}
                    mobile
                    onPress={() => handleTilePress(action.id)}
                  />
                ))}
            </View>
          </ScrollView>
        </View>
      </View>
    );
  }

  /* ── Default layout (web / iPad) ──────────────────────────── */
  return (
    <View style={styles.screenRoot}>
      <View style={styles.container}>
        <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
          <View style={styles.heroBlock}>
            <Text style={styles.heroGreeting}>{greeting}</Text>
            <Text style={styles.heroSubtitle}>{subtitle}</Text>
          </View>

          {accessRequestBanner}

          <Text style={styles.sectionLabel}>WHAT WOULD YOU LIKE TO DO?</Text>

          <View style={styles.gridContainer}>
            {(() => {
              const visible = resolvedActions.filter(a => {
                if (a.id !== 'my-tickets') return true;
                if (hasOpenTickets === null) {
                  return !!(myTicketsTotalElements && myTicketsTotalElements > 0);
                }
                return hasOpenTickets;
              });

              // If only Submit Ticket should be shown, render the form inline instead of the tile
              if (visible.length === 1 && visible[0].id === 'submit-ticket') {
                return (
                  <View style={{ flex: 1 }}>
                    <Suspense fallback={<LHLoader />}>
                      <SubmitTicketScreen onNavigate={navigateTo} isFirstTimeUser={hasOpenTickets === false} />
                    </Suspense>
                  </View>
                );
              }

              return visible.map((action: LandingActionView) => (
                <ActionTile
                  key={action.id}
                  action={action}
                  onPress={() => handleTilePress(action.id)}
                />
              ));
            })()}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const localStyles = StyleSheet.create({
  arBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#eff6ff",
    borderWidth: 1,
    borderColor: "#bfdbfe",
    borderRadius: 10,
    paddingHorizontal: 18,
    paddingVertical: 15,
    marginBottom: 22,
  },
  arBannerTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
  },
  arBannerSubtitle: {
    fontSize: 12,
    color: "#4b5563",
    marginTop: 3,
  },
  arBannerButton: {
    alignSelf: "center",
    marginLeft: 12,
    borderWidth: 1,
    borderColor: "#1e3a8a",
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  arBannerButtonText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#1e3a8a",
  },
  backHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    backgroundColor: "#ffffff",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  backText: {
    fontSize: 14,
    color: "#156fd5",
    fontWeight: "500",
  },
});

export default LimitedAccessPortal;
